import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import './ProfileGallery.css';

type ProfileData = {
  name?: string;
  homepageImage?: string;
  images?: string[];
  order?: number;
};

const ProfileGallery: React.FC = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!profileId) {
      setLoading(false);
      return;
    }
    const cacheKey = `profile_${profileId}`;
    const timeKey = `profile_${profileId}_time`;
    const maxAge = 30 * 60 * 1000; // 30 分钟缓存
    const cached = localStorage.getItem(cacheKey);
    const cacheTime = localStorage.getItem(timeKey);
    if (cached && cacheTime && Date.now() - parseInt(cacheTime) < maxAge) {
      try {
        setData(JSON.parse(cached) as ProfileData);
      } catch { }
      setLoading(false);
      return;
    }
    const fetchProfile = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'profiles', profileId));
        if (docSnap.exists()) {
          const profileData = docSnap.data() as ProfileData;
          setData(profileData);
          localStorage.setItem(cacheKey, JSON.stringify(profileData));
          localStorage.setItem(timeKey, Date.now().toString());
        } else {
          setError('Profile not found.');
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load profile.');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [profileId]);

  const images = data?.images ?? [];
  const goPrev = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : i <= 0 ? images.length - 1 : i - 1));
  }, [images.length]);
  const goNext = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : i >= images.length - 1 ? 0 : i + 1));
  }, [images.length]);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, closeLightbox, goPrev, goNext]);

  if (loading) {
    return (
      <div className="profile-gallery">
        <div className="profile-gallery-loading">Loading...</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="profile-gallery">
        <div className="profile-gallery-error">{error || 'Profile not found.'}</div>
      </div>
    );
  }

  const { name, homepageImage, images: imagesList = [] } = data;

  return (
    <div className="profile-gallery">
      {/* {name && <h1 className="profile-gallery-title">{name}</h1>} */}
      {homepageImage && (
        <div className="profile-gallery-hero">
          <img src={homepageImage} alt={name || 'Gallery'} />
        </div>
      )}
      {imagesList.length > 0 && (
        <div className="profile-gallery-grid">
          {imagesList.map((url, index) => (
            <div
              key={`${index}-${url}`}
              className="profile-gallery-grid-item"
              onClick={() => setLightboxIndex(index)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setLightboxIndex(index)}
              aria-label={`View image ${index + 1}`}
            >
              <img src={url} alt={`${name || 'Gallery'} ${index + 1}`} />
            </div>
          ))}
        </div>
      )}

      {/* Lightbox 放大预览 */}
      {lightboxIndex !== null && imagesList[lightboxIndex] && (
        <div className="profile-gallery-lightbox" onClick={closeLightbox} role="dialog" aria-modal="true">
          <button
            type="button"
            className="profile-gallery-lightbox-close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            <FaTimes size={24} />
          </button>
          {imagesList.length > 1 && (
            <>
              <button
                type="button"
                className="profile-gallery-lightbox-prev"
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                aria-label="Previous image"
              >
                <FaChevronLeft size={28} />
              </button>
              <button
                type="button"
                className="profile-gallery-lightbox-next"
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                aria-label="Next image"
              >
                <FaChevronRight size={28} />
              </button>
            </>
          )}
          <div className="profile-gallery-lightbox-content" onClick={e => e.stopPropagation()}>
            <img src={imagesList[lightboxIndex]} alt={`${name || 'Gallery'} ${lightboxIndex + 1}`} />
          </div>
          {imagesList.length > 1 && (
            <span className="profile-gallery-lightbox-counter">
              {lightboxIndex + 1} / {imagesList.length}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileGallery;
